import { api } from './index'

export type AmountType = 'FIXED' | 'PERCENT' | 'OPEN'

// The full handoff-mode union for the group filter axis — deliberately not
// api/revenueCenters.ts's narrower `Handoff` (no UNKNOWN/DINEIN), since that
// models a different wire contract; widening it would leak values across.
export type OrderHandoff =
  | 'UNKNOWN'
  | 'PICKUP'
  | 'DELIVERY'
  | 'CURBSIDE'
  | 'TABLESIDE'
  | 'DRIVETHRU'
  | 'DINEIN'

// Provider-reported criteria flags that determine when this charge applies.
// Read-only.
export type ToastCriteria = {
  delivery: boolean
  takeout: boolean
  dine_in: boolean
  min_check_amount: string
  max_check_amount: string
}

// The oneof — Toast is the only member today, so this is the whole shape.
export type ProviderCriteria = { toast?: ToastCriteria }

// Presence = force-applies; absence = defers to Toast's own criteria for
// that handoff. There is no explicit "force off".
export type CriteriaOverride = { handoffs: OrderHandoff[] }

export type ServiceCharge = {
  id: number
  provider_uid: string
  name: string
  amount_type: AmountType
  amount_value: number
  gratuity: boolean
  taxable: boolean
  thanx_enabled: boolean
  eligible_for_redemption: boolean
  provider_criteria: ProviderCriteria
  criteria_override: CriteriaOverride
  applicable_handoff_modes: OrderHandoff[]
}

// Not sent on the wire — a trivial derivation from a field already on the
// row, computed here instead of trusting a duplicated server value.
export const isOverrideLocked = (
  charge: Pick<ServiceCharge, 'provider_criteria'>
): boolean => {
  const toast = charge.provider_criteria.toast
  return Boolean(toast && (toast.delivery || toast.takeout || toast.dine_in))
}

// True when this charge should be flagged as delivery-applicable even
// though the provider hasn't marked it for delivery — an override that
// forces DELIVERY while the provider's own criteria stay silent on every
// handoff is exactly the gap FR #5's guardrail exists to surface.
export const isRecommendedForDelivery = (
  charge: Pick<ServiceCharge, 'provider_criteria' | 'criteria_override'>
): boolean => {
  const toast = charge.provider_criteria.toast
  if (toast?.delivery) return true
  const overridden = charge.criteria_override.handoffs.includes('DELIVERY')
  const toastSilent =
    !toast || (!toast.delivery && !toast.takeout && !toast.dine_in)
  return overridden && toastSilent
}

// Binary tiers plus unknown — there is no 'warning' tier.
export type ServiceChargeHealth = 'unknown' | 'healthy' | 'errored'

// Matches the verified response shape — location_health and synced_at are
// top-level siblings of data/meta, not nested inside meta. location_health is
// an array (one entry per requested location_id).
export type LocationServiceChargeHealth = {
  location_id: number
  health: ServiceChargeHealth
}

type GetServiceChargesResponse = {
  data: ServiceCharge[]
  meta: { page: number; per_page: number; total_count: number }
  location_health: LocationServiceChargeHealth[]
  synced_at: string | null
}

export type UpdateServiceChargePayload = {
  id: number
  location_id: number // sent on the wire — strengthens the server's accessibility check
  thanx_enabled?: boolean
  eligible_for_redemption?: boolean
  criteria_override?: CriteriaOverride
}

// Server-computed divergence flags (null/false treated as equivalent,
// compared key-by-key) — render exactly what's on the wire. Re-deriving
// these from raw per-location data would reintroduce the dineIn: false vs
// dineIn: null false-positive this server-side collapse exists to avoid.
export type ServiceChargeDivergence = {
  amount?: boolean
  criteria?: boolean
  criteria_override?: boolean
}

export type ServiceChargeGroup = {
  provider_uid: string
  name: string
  amount_type: AmountType
  location_count: number
  enabled_count: number
  override_delivery_count: number
  override_takeout_count: number
  divergence: ServiceChargeDivergence
  recommended_for_delivery: boolean
}

export type ServiceChargeGroupsMeta = {
  page: number
  per_page: number
  total_count: number
}

export type GetServiceChargeGroupsParams = {
  types?: AmountType[]
  search?: string
  // Tri-state (proto BoolValue semantics): undefined means "no filter" and
  // must never be sent on the wire — only `true`/`false` are real filters.
  enabled?: boolean
  handoffs?: OrderHandoff[]
  locationIds?: number[]
  page?: number
  perPage?: number
}

type GetServiceChargeGroupsResponse = {
  data: ServiceChargeGroup[]
  meta: ServiceChargeGroupsMeta
}

// One location's charge for a given group, read-only — a subset of
// ServiceCharge; group-level fields already known from the parent row
// aren't repeated. override_locked is derived via isOverrideLocked, not
// picked from the wire. The wire identifies the location only by
// location_id; callers must join against locationsApi.useGetLocationsQuery
// to render a display name.
export type ServiceChargeGroupLocation = Pick<
  ServiceCharge,
  | 'amount_type'
  | 'amount_value'
  | 'thanx_enabled'
  | 'provider_criteria'
  | 'criteria_override'
> & {
  location_id: number
}

type GetServiceChargeGroupLocationsResponse = {
  data: ServiceChargeGroupLocation[]
  meta: ServiceChargeGroupsMeta
}

export type GetServiceChargeGroupLocationsParams = {
  providerUid: string
  page?: number
  perPage?: number
}

// Mirrors revenueCenters.ts's include_states[] idiom: fetchBaseQuery's
// `params` stringifies arrays as comma-joined (not one entry per value), so
// array params are hand-built onto URLSearchParams instead.
const buildServiceChargeGroupsQuery = ({
  types = [],
  search,
  enabled,
  handoffs = [],
  locationIds = [],
  page = 1,
  perPage = 25,
}: GetServiceChargeGroupsParams): string => {
  const params = new URLSearchParams()
  types.forEach(type => params.append('types[]', type))
  if (search) params.append('search', search)
  // Tri-state: only append when the caller actually chose a value. Sending
  // `enabled=false` for an unset filter would silently filter out every
  // enabled charge.
  if (enabled !== undefined) params.append('enabled', String(enabled))
  handoffs.forEach(handoff => params.append('handoffs[]', handoff))
  locationIds.forEach(id => params.append('location_ids[]', String(id)))
  params.append('page', String(page))
  params.append('per_page', String(perPage))
  return params.toString()
}

export const serviceChargesApi = api.injectEndpoints({
  overrideExisting: false,
  endpoints: build => ({
    /**
     * Lists a single location's Service Charge catalog plus the response-level
     * health/sync metadata for that location. Tagged per locationId (not the
     * bare 'ServiceCharge' tag) so refetching one location's charges doesn't
     * invalidate every other location's cached list.
     */
    getServiceCharges: build.query<
      GetServiceChargesResponse,
      { locationId: number }
    >({
      query: ({ locationId }) => ({
        url: 'service_charges',
        params: { location_id: locationId },
      }),
      providesTags: (_result, _error, { locationId }) => [
        { type: 'ServiceCharge', id: locationId },
      ],
    }),

    /**
     * Updates one charge's merchant-writable fields (thanx_enabled,
     * eligible_for_redemption, and/or criteria_override), identified by id.
     * location_id is also sent — it's not needed to find the row, but
     * strengthens the server's accessibility check.
     */
    updateServiceCharge: build.mutation<void, UpdateServiceChargePayload>({
      query: body => ({
        url: 'service_charges',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { location_id }) => [
        { type: 'ServiceCharge', id: location_id },
        // Bare tag (no id) so any query providing it — the merchant-wide
        // group list/drill-down — refetches too, not just this location's
        // own list.
        'ServiceChargeGroup',
      ],
    }),

    /**
     * Triggers an asynchronous re-sync of Service Charges from the ordering
     * provider, scoped to the given location — Service Charges are a
     * per-location resource, unlike merchant-wide Revenue Centers. Returns
     * 202 immediately with no charge data in the response; the catalog and
     * location_health/synced_at update once the background job
     * completes, so the tag invalidation (not the response body) is what
     * drives the refetch.
     */
    refreshServiceCharges: build.mutation<void, { locationId: number }>({
      query: ({ locationId }) => ({
        url: 'ordering_config/refresh',
        method: 'POST',
        body: { configs: ['SERVICE_CHARGES'], location_ids: [locationId] },
      }),
      invalidatesTags: (_result, _error, { locationId }) => [
        { type: 'ServiceCharge', id: locationId },
        'ServiceChargeGroup',
      ],
    }),

    /**
     * Lists the merchant's distinct Service Charge groups (one row per
     * provider_uid across all locations), scoped server-side to the acting
     * user's accessible locations. A read-only projection over the
     * location-level Service Charge data.
     */
    getServiceChargeGroups: build.query<
      GetServiceChargeGroupsResponse,
      GetServiceChargeGroupsParams
    >({
      query: filters =>
        `service_charge_groups?${buildServiceChargeGroupsQuery(filters)}`,
      providesTags: ['ServiceChargeGroup'],
    }),

    /**
     * Lists one group's per-location charges (read-only drill-down),
     * filtered server-side to the acting user's accessible locations.
     */
    getServiceChargeGroupLocations: build.query<
      GetServiceChargeGroupLocationsResponse,
      GetServiceChargeGroupLocationsParams
    >({
      query: ({ providerUid, page = 1, perPage = 25 }) => {
        const params = new URLSearchParams()
        params.append('page', String(page))
        params.append('per_page', String(perPage))
        return `service_charge_groups/${encodeURIComponent(
          providerUid
        )}/locations?${params.toString()}`
      },
      providesTags: ['ServiceChargeGroup'],
    }),
  }),
})

export const {
  useGetServiceChargesQuery,
  useUpdateServiceChargeMutation,
  useRefreshServiceChargesMutation,
  useGetServiceChargeGroupsQuery,
  useGetServiceChargeGroupLocationsQuery,
} = serviceChargesApi
